import { Column } from "@tento-ui/component-builtin";
import { QuickAddCard } from "@tento-ui/components/quick-add/quick-add-card";
import { SpotlightLayout } from "@tento-ui/components/spotlight/spotlight-layout";
import { SpotlightRow } from "@tento-ui/components/spotlight/spotlight-row";
import { Storybook } from "@tento-ui/ui/storybook";
import { SpotlightIcons } from "@tento-ui/ui/spotlight-icons";
import {
  EventClassifierLoader,
  type EventCategory,
} from "@tento-lona";
import { LonaResources } from "../../resources";
import { STORYBOOK_EVENT_PRESENTATION } from "../storybook-presentation";

// The gallery's own classifier (no app client here): the same trained weights
// the calendar app's client loads.
const classifier = new EventClassifierLoader(() =>
  LonaResources.current.bytes("/@/static/models/events_classifier.bin")
);

// Storybook harness for the live event-category suggestion, exercised through
// the real spotlight (no separate input). Type into the spotlight's own search
// bar and watch — in realtime, on every keystroke — the status line report what
// the title is being classified into, while the quick-add result updates. The
// result is the real `LonaEventsQuickAddRow` shape: a std-spotlight-row (﹢ icon
// + "Quick add: Create an Event" title) whose subtitle is the QuickAddCard, with
// its suggested-category chip driven by the same on-device classifier + mapping
// the production spotlight uses (calendar.routes/spotlight.ts `quickAddRow`). The
// chip abstains below the confidence gate; the status still shows the raw verdict
// + top-3 breakdown. The model loader + the event-color theme resolver are
// configured in ui.routes/examples.ts.
export class QuickAddCardStoryBook extends Storybook.Default {
  title(): string {
    return "Quick Add Card";
  }

  description(): Option<string> {
    return "Type in the spotlight; the status reports the live classification in realtime.";
  }

  examples(): Storybook.Example[] {
    return [
      { title: "Spotlight (live classifier)", render: () => this.spotlight() },
      { title: "Suggested: work", render: () => this.preset("work") },
      { title: "Suggested: exercise", render: () => this.preset("exercise") },
      { title: "No suggestion", render: () => this.preset(null) },
    ];
  }

  // The same suggestOpt → itemOpt → chip mapping as spotlight `quickAddRow`.
  private chipFor(name: Option<EventCategory>): Option<QuickAddCard.Category> {
    const item = STORYBOOK_EVENT_PRESENTATION.categories.itemOpt(name ?? null);
    return item
      ? { emoji: item.emoji, name: item.name, color: item.colorOpt() ?? "var(--gray)" }
      : null;
  }

  private async spotlight(): Promise<Storybook.RenderResult> {
    // Guarantee the model is loaded before the spotlight is shown so the first
    // keystroke already has a classifier to query.
    await classifier.ensure();

    const minConfidence = 0.35;
    const pct = (p: number) => `${Math.round(p * 100)}%`;

    const $status = Storybook.Card.StatusLine.make();

    // The quick-add result, mirroring the real spotlight's LonaEventsQuickAddRow:
    // a std-spotlight-row (plus icon + title) whose subtitle is the QuickAddCard,
    // re-bound on every keystroke. The card coalesces its chip write to a single
    // fastdom frame, so this is safe to drive with no debounce.
    const $card = QuickAddCard.make();
    // Mirror LonaEventsQuickAddRow's `#title` spacing above the card.
    const $title = SpotlightRow.$title("Quick add: Create an Event");
    $title.style.setProperty("--margin-top", "6px");
    $title.style.setProperty("--margin-bottom", "8px");
    const $row = SpotlightRow.make().bind(
      $title,
      SpotlightIcons.text("﹢"),
      $card,
      async () => {},
    );

    const $spotlight = SpotlightLayout.make();
    $spotlight.style.width = "100%";

    // The handler: the spotlight's OWN search bar drives this on every keystroke
    // (SpotlightLayout `onSearchTextChange`). Classify the query, report the live
    // verdict, and surface the quick-add row (with its suggested chip) as the top
    // result — exactly the flow `quickAddRow` runs in production.
    const onQuery = (query: Option<string>) => {
      const title = (query ?? "").trim();

      const scores =
        title.length === 0
          ? []
          : (classifier.current()?.classifyWithScores(title) ?? []);
      const top = scores[0];
      const suggested = top && top[1] >= minConfidence ? top[0] : null;

      if (!top) {
        $status.bindText("Type a title to classify…");
        $spotlight.setResults([], []);
        return;
      }

      const [category, confidence] = top;
      const verdict =
        confidence >= minConfidence
          ? `Classified as: ${category} (${pct(confidence)})`
          : `Best guess: ${category} (${pct(confidence)}) — below ${pct(minConfidence)}, no chip`;
      $status.bindText([
        verdict,
        scores
          .slice(0, 3)
          .map(([c, p]) => `${c} ${pct(p)}`)
          .join("   ·   "),
      ]);

      $card.bind({ title, startLabel: "7:00 AM", endLabel: "8:00 AM" });
      $card.bindCategory(this.chipFor(suggested));
      $spotlight.setResults([{ $e: $row, onSelect: () => {} }], []);
    };

    $spotlight.bindPlaceholder("9am gym · lunch w/ Sarah · flight to SFO");
    $spotlight.setCallbacks({
      ...SpotlightLayout.NOOP_CALLBACKS,
      onSearchTextChange: onQuery,
    });

    // Prime the preview with a sample classification so the row + verdict are
    // visible before the user types into the search bar.
    onQuery("yoga");

    const $container = Column.make();
    $container.style.width = "380px";
    $container.style.minHeight = "320px";
    $container.appendChild($spotlight);

    return { $content: $container, $status };
  }

  private preset(name: Option<EventCategory>): HTMLElement {
    const $card = QuickAddCard.makeWith({
      title: name ?? "untitled",
      startLabel: "7:00 AM",
      endLabel: "8:00 AM",
    });
    $card.bindCategory(this.chipFor(name));
    return $card;
  }
}
